import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  Input,
  FormGroup,
  Button,
  ButtonGroup,
  Select as SPAUISelect,
  Switch,
  OverlayTrigger,
  PopOver,
  Checkbox,
  CheckboxGroup,
  TimePicker,
  message,
} from '@tencent/spaui';
import get from 'lodash/get';

import '../../../../style/less/modules/qw-drainage.less';
import { RPCFactory } from '../../../utils/RequestUtil';
import { SelectionItemComponent } from '../qwCustomerList/ownerPicker';
import { beaconInstance } from '../../../instrumentation/beaconSetup';

const EMPTY_ARR = [];
const EMPTY_OBJ = {};
const EMPTY_TIMER = [[]];

const TIME_TYPE = {
  full: 'full',
  custom: 'custom',
};

const LIMIT_MODE = {
  single: 'single',
  batch: 'batch',
};

const PAGE_MODE = {
  create: 'create',
  detail: 'detail',
  edit: 'edit',
};

const halfHourIntervalList = (() => {
  const arr = [];
  for (let i = 0; i < 24; i++) {
    const hour = `00${i}`.slice(-2);
    arr.push(`${hour}:00`);
    arr.push(`${hour}:30`);
  }
  return arr;
})();

const getGroupName = (index) => {
  const chineseNumber = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
  return `客服${chineseNumber[index]}组`;
};

const checkTimeCross = (arr) => {
  if (arr.length < 1) return false;
  const starts = arr.map((item) => item[0]).sort();
  const ends = arr.map((item) => item[1]).sort();
  for (let i = 1; i < starts.length; i++) {
    if (starts[i] < ends[i - 1]) return false;
  }
  return true;
};

const Head = ({ mode }) => {
  const title = useMemo(() => {
    if (mode === PAGE_MODE.edit) return '编辑渠道活码';
    if (mode === PAGE_MODE.detail) return '渠道活码详情';
    return '新建渠道活码';
  }, [mode]);

  useEffect(() => {
    const menu3rd = document.querySelector('.sub-main > .sec-top-tab');
    if (!menu3rd) return;
    menu3rd.setAttribute('style', 'display: none;');
    return () => {
      menu3rd.removeAttribute('style');
    };
  }, []);

  return (
    <div className="sec sec-top-tab">
      <div className="sec-breadcrumb radius-collapse">
        <div className="breadcrumb">
          <a href="#/qw/acquisition/drainage" className="breadcrumb-back" role="button">
            <i className="icon icon-breadcrumb-back"></i>
          </a>
          <ol role="navigation" aria-label="breadcrumbs" className="breadcrumbs">
            <li>
              <a role="button" href="#/qw/acquisition/drainage">引流获客</a>
            </li>
            <li>
              <a className="current">{title}</a>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

const Feedback = ({ max, curr }) => {
  if (!max) return null;
  return (
    <span>
      <span className="spaui-input-feedback-count">{curr}</span>
      <span className="spaui-input-feedback-divider">/</span>
      <span className="spaui-input-feedback-range">{max}</span>
    </span>
  );
};

const useInput = ({
  required = false,
  defaultValue = '',
  max = 0,
  placeholder = '',
  disabled,
  testid,
} = {}) => {
  const [value, setValue] = useState(defaultValue);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let val = defaultValue;
    if (max > 0) val = defaultValue.slice(0, max);
    setValue(val);
    setStatus('');
  }, [defaultValue, max]);

  const onChange = (e, val) => {
    if (max > 0 && val.length > max) return;
    setValue(val);
    setStatus('');
  };

  const onFocus = () => {
    setStatus('');
  };

  const onBlur = () => {
    if (!required) return;
    if (value.length < 1) setStatus('error');
  };

  const check = () => {
    const errors = [];
    if (required && value.length < 1) errors.push('value is empty');
    if (max > 0 && value.length > max) errors.push('value length over max limit');
    if (errors.length > 0) setStatus('error');
    return errors.length < 1;
  };

  return {
    value,
    check,
    props: {
      max,
      value,
      status,
      placeholder,
      onChange,
      onFocus,
      onBlur,
      disabled,
      testid,
    },
  };
};

const LimitedInput = ({
  max,
  value,
  status,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  disabled,
  testid,
}) => {
  return (
    <FormGroup
      feedback={<Feedback key={0} max={max} curr={value.length} />}
      status={status}
    // helpBlock={help}
    >
      <Input
        useComponsition
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        maxLength={max}
        data-testid={testid}
      />
    </FormGroup>
  );
};

const useSelect = ({
  required = false,
  defaultValue = '',
  placeholder = '',
  multiple = false,
  options = [],
  disabled,
  testid,
  onChange: _onChange,
} = {}) => {
  const [value, setValue] = useState(defaultValue);
  const [status, setStatus] = useState('');

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onChange = (e, val) => {
    setValue(val);
    setStatus('');
    if (typeof _onChange === 'function') _onChange(val);
  };

  const check = () => {
    if (!required) return true;
    if (required && value.length < 1) setStatus('error');
    return value.length > 0;
  };

  return {
    value,
    check,
    props: {
      value,
      options,
      status,
      multiple,
      placeholder,
      onChange,
      disabled,
      testid,
    },
  };
};

const ValueAreaComponent = (props) => {
  const { value, onOpen, placeholder } = props;

  const ids = [value].flat().filter(Boolean);

  let valueArea = (
    <div className="selection-single-text">
      <span className="tt">
        <div className="in">{placeholder}</div>
      </span>
    </div>
  );

  if (ids.length > 0) valueArea = (
    <span className="selection-single-text">
      {
        ids.map((id) => (
          <span className="qw-name-component" key={id}>
            <ww-open-data type="userName" key={id} openid={id} />
          </span>
        ))
      }
    </span>
  );

  return (
    <div className={`selection-single${ids.length < 1 ? ' placeholder' : ''}`} onClick={onOpen}>
      {valueArea}
      <div className="selection-arrow">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path fillRule="evenodd" clipRule="evenodd" fill="currentColor" d="M12.42 5.92a.25.25 0 00-.354 0L8 9.984 3.934 5.92a.25.25 0 00-.353 0l-.354.354a.25.25 0 000 .354L7.47 10.87l.353.354a.25.25 0 00.354 0l.353-.354 4.243-4.242a.25.25 0 000-.354l-.354-.354z" />
        </svg>
      </div>
    </div>
  );
};

const Select = ({
  value,
  options,
  status,
  multiple,
  placeholder,
  onChange,
  disabled,
  testid,
  style = {},
  autoSearch = true,
}) => {
  const searchProps = {};
  if (!autoSearch) searchProps.search = false;
  return (
    <FormGroup status={status} style={style}>
      <SPAUISelect
        value={value}
        data={options}
        multiple={multiple}
        onChange={onChange}
        placeholder={placeholder}
        SelectionItemComponent={SelectionItemComponent}
        ValueAreaComponent={ValueAreaComponent}
        disabled={disabled}
        data-testid={testid}
        {...searchProps}
      />
    </FormGroup>
  );
};

const useSwitch = ({
  defaultValue = false,
  disabled,
  testid,
  onChange: _onChange,
} = {}) => {
  const [checked, setChecked] = useState(defaultValue);

  useEffect(() => {
    setChecked(defaultValue);
  }, [defaultValue]);

  const onChange = (e, val) => {
    setChecked(val);
    if (typeof _onChange === 'function') _onChange(val);
  };

  return {
    active: checked,
    props: {
      checked,
      onChange,
      disabled,
      'data-testid': testid,
    },
  };
};

const useTimeChecker = ({
  defaultValue = EMPTY_TIMER,
  disabled,
} = {}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onStartChange = (index, val) => {
    setValue((arr) => {
      const indexItem = arr[index].concat();
      indexItem[0] = val;
      const newArr = arr.concat();
      newArr[index] = indexItem;
      return newArr;
    });
  };

  const onEndChange = (index, val) => {
    setValue((arr) => {
      const indexItem = arr[index].concat();
      indexItem[1] = val;
      const newArr = arr.concat();
      newArr[index] = indexItem;
      return newArr;
    });
  };

  const onRemove = (index) => {
    setValue((arr) => {
      const newArr = arr.concat();
      newArr.splice(index, 1);
      return newArr
    });
  };

  const onAdd = () => {
    setValue((arr) => [...arr, []]);
  };

  return {
    value,
    props: {
      value,
      onStartChange,
      onEndChange,
      onRemove,
      onAdd,
      disabled
    },
  };
};

const TimeChecker = ({
  value = EMPTY_TIMER,
  onStartChange,
  onEndChange,
  onRemove,
  onAdd,
  disabled,
}) => {
  const getPositionTime = (time, position) => {
    const index = halfHourIntervalList.indexOf(time);
    if (index === -1) return undefined;
    return halfHourIntervalList[index + position]
  };

  return value.map(([start, end], i) => (
    <div className="dek-time-checker" key={i}>
      <TimePicker
        value={start}
        endTime={getPositionTime(end, -1)}
        timeList={halfHourIntervalList}
        onChange={(e, val) => onStartChange(i, val)}
        disabled={disabled}
      />
      <span style={{ margin: '0 12px ' }}>至</span>
      <TimePicker
        value={end}
        beginTime={getPositionTime(start, 1)}
        timeList={halfHourIntervalList}
        onChange={(e, val) => onEndChange(i, val)}
        disabled={disabled}
      />
      {
        disabled ? null : (i === value.length - 1) && value.length < 5 ? (
          <i className="adder" onClick={onAdd} />
        ) : (
          <i className="subtracter" onClick={() => onRemove(i)} />
        )
      }
    </div>
  ));
};

const KfItem = ({
  options = EMPTY_ARR,
  data = EMPTY_OBJ,
  onChange,
  index,
  disabled,
}) => {

  const service = useSelect({
    required: true,
    multiple: true,
    options: options,
    placeholder: '客服人员可以选择多个',
    defaultValue: data.employees,
    disabled,
  });
  const timeType = useSelect({
    defaultValue: data.timeType,
    options: [
      { value: TIME_TYPE.full, label: '全天在线' },
      { value: TIME_TYPE.custom, label: '自定义时间' },
    ],
    disabled,
    onChange: (val) => {
      if (val === TIME_TYPE.custom) {
        beaconInstance.onUserAction(
          'btn_click',
          { page: '渠道活码', name: '点击按自定义上下线时间按钮' },
        );
      }
    },
  });
  const weeks = useSelect({
    defaultValue: data.weeks || EMPTY_ARR,
    options: [
      { value: 1, label: '周一' },
      { value: 2, label: '周二' },
      { value: 3, label: '周三' },
      { value: 4, label: '周四' },
      { value: 5, label: '周五' },
      { value: 6, label: '周六' },
      { value: 7, label: '周日' },
    ],
    disabled,
  });
  const period = useTimeChecker({
    defaultValue: data.period || EMPTY_TIMER,
    disabled,
  });

  useEffect(() => {
    const val = {
      employees: service.value,
      timeType: timeType.value,
      weeks: weeks.value,
      period: period.value,
    };
    onChange(index, val);
  }, [
    index,
    service.value,
    timeType.value,
    weeks.value,
    period.value,
    onChange,
  ]);

  return (
    <div className="dek-box">
      <div className="dek-box-title">{getGroupName(index)}</div>
      <Select
        {...service.props}
        style={{ marginBottom: 17 }}
        status={data.employeeStatus}
        autoSearch={false}
      />
      <div className="dek-box-title">在线时间</div>
      <ButtonGroup
        activeDisplayType="primary"
        data={timeType.props.options}
        value={timeType.props.value}
        onChange={timeType.props.onChange}
        disabled={timeType.props.disabled}
      />
      {
        timeType.value === TIME_TYPE.custom && (
          <div>
            <div className="dek-group-line">
              <div className="dek-group-line-label">周期</div>
              <div className="dek-group-line-content">
                <CheckboxGroup
                  data={weeks.props.options}
                  value={weeks.props.value}
                  onChange={weeks.props.onChange}
                  disabled={weeks.props.disabled}
                />
              </div>
            </div>
            <div className="dek-group-line">
              <div className="dek-group-line-label">时间</div>
              <div className="dek-group-line-content">
                <TimeChecker {...period.props} />
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

const useKfGroup = ({
  defaultValue = EMPTY_TIMER,
  options = EMPTY_ARR,
  disabled,
} = {}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onItemChange = useCallback((index, val) => {
    if (val.employees.length > 0) val.employeeStatus = '';
    setValue((arr) => {
      const newArr = [...arr];
      newArr[index] = val;
      return newArr;
    });
  }, []);

  const onItemAdd = (e) => {
    if (typeof e.preventDefault === 'function') e.preventDefault();
    setValue((arr) => [
      ...arr,
      {
        employees: [],
        timeType: TIME_TYPE.full,
        weeks: [],
        period: [[]],
      },
    ]);
  };

  const check = () => {
    const errors = [];
    value.forEach((item, index) => {
      const groupName = getGroupName(index);
      if (item.employees.length < 1) {
        errors.push(`${groupName}: 请选择客服人员`);
      }
      // 全天在线的情况下无需校验周期和时间
      if (item.timeType === TIME_TYPE.full) return;
      if (item.weeks.length < 1) {
        errors.push(`${groupName}: 请选择周期`)
      }
      const hasEmptyPeriod = item.period.some((arr) => arr.filter(Boolean).length < 2);
      const hasCross = !checkTimeCross(item.period.filter((item) => item.length === 2));
      if (hasEmptyPeriod || hasCross) {
        errors.push(`${groupName}: 请检查时间是否填写完整或者有交叉存在`);
      }
    });
    // 这里需要手动设置客服组客服人员为空的错误状态
    setValue((arr) => {
      return arr.map((item) => {
        const _item = { ...item };
        if (_item.employees.length < 1) {
          _item.employeeStatus = 'error';
        }
        return _item;
      });
    });
    if (errors.length > 0) alert(errors.join('\n'));
    return errors.length < 1;
  };

  const selectedEmployees = useMemo(() => {
    const employees = value.map((item) => item.employees).flat();
    return [...new Set(employees)];
  }, [value]);

  return {
    value,
    check,
    selectedEmployees,
    props: {
      value,
      options,
      onItemChange,
      onItemAdd,
      disabled,
    },
  };
};

const KfGroup = ({
  value,
  options,
  onItemChange,
  onItemAdd,
  disabled,
}) => {

  return (
    <div className="drainage-editor-kfgroup">
      {
        value.map((item, i) => (
          <KfItem
            key={i}
            index={i}
            data={item}
            options={options}
            onChange={onItemChange}
            disabled={disabled}
          />
        ))
      }
      {
        !disabled && value.length < 5 && (
          <div className="dek-box-adder">
            <a href="#" onClick={onItemAdd}>+ 添加客服组</a>
          </div>
        )
      }
    </div>
  );
};

const useUpperLimit = ({
  defaultActive = false,
  defaultValue = EMPTY_ARR,
  ignore = '',
  disabled,
  testid,
} = {}) => {
  const activeHook = useSwitch({
    defaultValue: defaultActive,
    disabled,
    testid,
    onChange: (val) => {
      if (!val) return;
      beaconInstance.onUserAction(
        'btn_click',
        { page: '渠道活码', name: '点击开启员工上限按钮' },
      );
    },
  });
  const [value, setValue] = useState(defaultValue);
  const [mode, setMode] = useState(LIMIT_MODE.single);
  const [editingItem, setEditingItem] = useState('');
  const [batchNum, setBatchNum] = useState('');
  const [batchSelected, setBatchSelected] = useState([]);

  const jsonStr = JSON.stringify(defaultValue);
  useEffect(() => {
    setValue(JSON.parse(jsonStr));
  }, [jsonStr]);

  const onModeChange = (val) => {
    if (!(val in LIMIT_MODE)) return;
    setMode(val);
    setBatchNum('');
    setBatchSelected([]);
  };

  const startEditItem = (id) => {
    id = typeof id === 'string' ? id : '';
    setEditingItem(id);
  };

  const onLimitChange = (id, val) => {
    if (val && !/^\d*$/.test(val)) return;
    val = parseInt(val || 0);
    if (typeof val !== 'number') return;
    setValue((arr) => arr.map((item) => {
      if (item.id !== id) return item;
      return { ...item, limit: val };
    }));
  };

  const onBatchNumChange = ({ target }) => {
    let val = target.value;
    if (!/^[1-9]\d*$/.test(val)) return;
    val = parseInt(val); if (typeof val !== 'number') return;
    setBatchNum(val);
  };

  const onBatchConfirm = () => {
    onModeChange(LIMIT_MODE.single);
    setValue((arr) => arr.map((item) => {
      if (!batchSelected.includes(item.id)) return item;
      return { ...item, limit: parseInt(batchNum) };
    }));
  };

  const onBatchSelect = (id) => {
    if (mode === LIMIT_MODE.single) return;
    setBatchSelected((arr) => {
      const set = new Set(arr);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  useEffect(() => {
    if (mode === LIMIT_MODE.batch || !editingItem) return;
    const element = document.querySelector(`.dul-item [title=${editingItem}] + input`);
    if (!element) return;
    element.select && element.select();
  }, [editingItem, mode]);

  const filted = value.filter((item) => item.id !== ignore);

  return {
    active: activeHook.active,
    value,
    props: {
      activeProps: activeHook.props,
      value: filted,
      mode,
      onModeChange,
      startEditItem,
      editingItem,
      onLimitChange,
      batchNum,
      onBatchNumChange,
      onBatchConfirm,
      batchSelected,
      onBatchSelect,
      disabled,
      testid,
    },
  };
};

const UpperLimit = ({
  activeProps,
  value,
  mode,
  onModeChange,
  startEditItem,
  editingItem,
  onLimitChange,
  batchNum,
  onBatchNumChange,
  onBatchConfirm,
  batchSelected,
  onBatchSelect,
  disabled,
}) => {
  const handleBatchEdit = (e) => {
    e.preventDefault();
    onModeChange(LIMIT_MODE.batch);
  };

  const handleBatchCancel = (e) => {
    e.preventDefault();
    onModeChange(LIMIT_MODE.single);
  };

  const handleBatchConfirm = (e) => {
    e.preventDefault();
    onBatchConfirm();
  };

  return (
    <div className={`drainage-upper-limit ${mode}`}>
      <div style={{ display: 'flex', alignItems: 'center', height: 36 }}>
        <Switch {...activeProps} />
        <span style={{ marginLeft: 8 }}>{activeProps.checked ? '开启' : '已关闭'}</span>
      </div>
      {
        activeProps.checked && (
          <div className="dul-container">
            <div className="dul-content">
              {
                value.map((item) => (
                  <div className="dul-item" key={item.id} onClick={() => onBatchSelect(item.id)}>
                    {
                      !disabled && (
                        <Checkbox
                          checked={batchSelected.includes(item.id)}
                          style={{ pointerEvents: 'none' }}
                        />
                      )
                    }
                    <span className="dul-name" title={item.id}>
                      <ww-open-data type="userName" openid={item.id} />
                    </span>
                    {
                      !disabled && item.id === editingItem ? (
                        <Input
                          size="small"
                          placeholder="输入人数"
                          style={{ width: 71 }}
                          className="dul-num-editor"
                          onBlur={() => startEditItem('')}
                          value={item.limit || ''}
                          onChange={(e, val) => onLimitChange(item.id, val)}
                        />
                      ) : (
                        <span className="dul-num editable" onClick={() => startEditItem(item.id)}>
                          <span>{item.limit > 0 ? `${item.limit} 人` : '无限制'}</span>
                          {!disabled && (<i className="dul-icon-edit"></i>)}
                        </span>
                      )
                    }
                  </div>
                ))
              }
            </div>
            {
              !disabled && mode === LIMIT_MODE.single && (
                <div className="dul-footer">
                  <a href="#" className="dul-btn-batch" onClick={handleBatchEdit}>批量设置</a>
                </div>
              )
            }
            {
              !disabled && mode === LIMIT_MODE.batch && (
                <div className="dul-footer">
                  <input
                    type="text"
                    style={{ paddingLeft: 12 }}
                    placeholder="请输入上限数（人）"
                    value={batchNum}
                    onChange={onBatchNumChange}
                  />
                  <a href="#" className="dul-btn-cancel" onClick={handleBatchCancel}>取消</a>
                  <a href="#" onClick={handleBatchConfirm}
                    className={`dul-btn-confirm${batchNum && batchSelected.length > 0 ? '' : ' disable'}`}
                  >
                    确定
                  </a>
                </div>
              )
            }
          </div>
        )
      }
    </div>
  );
};

const Footer = ({ onSubmit, onCancel, mode }) => {
  if (mode === PAGE_MODE.detail) return (
    <div className="drainage-editor-footer" style={{ margin: '12px 0' }}>
      <Button style={{ marginRight: 12 }} onClick={onCancel}>返回</Button>
    </div>
  );
  return (
    <div className="drainage-editor-footer" style={{ margin: '12px 0' }}>
      <Button style={{ marginRight: 12 }} onClick={onCancel}>取消</Button>
      <Button displayType="primary" onClick={onSubmit}>
        {mode === PAGE_MODE.edit ? '确定更新' : '确定新建'}
      </Button>
    </div>
  );
};

const Body = ({ history, match, mode }) => {
  const [data, setData] = useState({
    codeName: '',
    serviceGroup: [
      {
        employees: [],
        employeeStatus: '',
        timeType: TIME_TYPE.full,
        weeks: [],
        period: [[]],
      },
    ],
    backupMember: '',
    activeLimit: false,
    limits: [
      // {
      //   id: 'wo7eXwDAAAWITuxVy5VZdUv01eYcRpUg',
      //   limit: 50,
      // },
    ],
    supplement: false,
  });
  const [employeeList, setEmployeeList] = useState([]);

  const employeeOption = useMemo(() => {
    return employeeList.map((item) => ({
      label: item.wxWorkUserId,
      value: item.wxWorkUserId,
    }));
  }, [employeeList]);

  const codeId = useMemo(() => {
    return match.params.id;
  }, [match]);

  const getLimitedId = (id) => {
    const map = {};
    (get(data, 'limits') || []).forEach((item) => {
      map[item.id] = item.limit;
    });
    return { id, limit: map[id] || 0 };
  };

  const codeName = useInput({
    required: true,
    max: 10,
    defaultValue: data.codeName,
    placeholder: '请输入二维码名称',
    disabled: mode === PAGE_MODE.detail,
    testid: 'codename',
  });
  const backupMember = useSelect({
    required: true,
    defaultValue: data.backupMember,
    placeholder: '请选择备用员工',
    options: employeeOption,
    disabled: mode === PAGE_MODE.detail,
    testid: 'backup',
  });
  const supplement = useSwitch({
    defaultValue: data.supplement,
    disabled: mode === PAGE_MODE.detail,
    testid: 'buqi',
    onChange: (val) => {
      if (!val) return;
      beaconInstance.onUserAction(
        'btn_click',
        { page: '渠道活码', name: '点击补齐分配按钮' },
      );
    },
  });
  const serviceGroup = useKfGroup({
    options: employeeOption,
    defaultValue: data.serviceGroup,
    disabled: mode === PAGE_MODE.detail,
  });
  const dv = serviceGroup.selectedEmployees.map(getLimitedId);
  const upperLimit = useUpperLimit({
    defaultActive: data.activeLimit,
    defaultValue: dv,
    ignore: backupMember.value,
    disabled: mode === PAGE_MODE.detail,
    testid: 'limit',
  });

  const handleCancel = () => {
    history.replace('/qw/acquisition/drainage');
  };

  const validate = () => {
    const errors = [];
    if (!codeName.check()) errors.push('请填写二维码名称');
    if (!serviceGroup.check()) errors.push('请检查客服人员设置');
    if (!backupMember.check()) errors.push('请选择备用员工');
    if (errors.length > 0) console.warn('submit errors:', errors);
    return errors.length < 1;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const params = {
      name: codeName.value,
      distributeStrategy: supplement.active ? 1 : 0,
      backupWxWorkUserId: backupMember.value,
      enableExternalUserLimitation: upperLimit.active ? 1 : 0,
      userConfigGroups: serviceGroup.value.map((item, index) => {
        return {
          name: getGroupName(index),
          wxWorkUserIds: item.employees,
          onlineStrategy: item.timeType === TIME_TYPE.custom ? 1 : 0,
          weekPeriod: item.weeks,
          timeRange: item.period.map((subItem) => ({
            startTime: subItem[0],
            endTime: subItem[1],
          })),
        };
      }),
    };
    if (upperLimit.active) {
      params.externalUserLimitations = upperLimit.value.map((item) => ({
        wxWorkUserId: item.id,
        upperLimitation: item.id === backupMember.value ? 0 : item.limit,
      }));
    }
    if (mode === PAGE_MODE.create) {
      RPCFactory.qwDrainage.create(params)
        .done(() => {
          message.success('创建成功');
          handleCancel();
        })
        .fail((ret, msg) => alert(msg));
    } else if (mode === PAGE_MODE.edit) {
      params.id = codeId;
      RPCFactory.qwDrainage.update(params)
        .done(() => {
          message.success('创建成功');
          handleCancel();
        })
        .fail((ret, msg) => alert(msg));
    }
  };

  useEffect(() => {
    if (mode === PAGE_MODE.create || !codeId) return;
    RPCFactory.qwDrainage.getDetail(codeId)
      .done((res) => {
        const getPeriod = (arr) => {
          if (!Array.isArray(arr) || arr.length < 1) return [[]];
          return arr.map((item) => [item.startTime, item.endTime]);
        };
        const param = {
          codeName: res.name,
          backupMember: res.backupWxWorkUserId,
          supplement: res.distributeStrategy === 1,
          activeLimit: res.enableExternalUserLimitation === 1,
          limits: (res.externalUserLimitations || []).map((item = {}) => ({
            id: item.wxWorkUserId,
            limit: item.upperLimitation || 0,
          })),
          serviceGroup: (res.userConfigGroups || []).map((item = {}) => ({
            employees: item.wxWorkUserIds || [],
            employeeStatus: '',
            timeType: item.onlineStrategy === 1 ? TIME_TYPE.custom : TIME_TYPE.full,
            weeks: item.weekPeriod || [],
            period: getPeriod(item.timeRange),
          })),
        };
        setData(param);
      });
  }, [mode, codeId]);

  useEffect(() => {
    let total = -1;
    let arr = [];
    // 递归拉取到所有的员工，pagesize最大100
    const fetchFn = (page = 1) => {
      RPCFactory.qwEmployee.getList({
        pageSize: 100,
        pageIndex: page,
      }).done((res) => {
        if (total < 0) total = res.size;
        arr = arr.concat(res.entities || []);
        if (arr.length < total) fetchFn(page + 1);
        else setEmployeeList(arr);
      }).fail(() => setEmployeeList(arr));
    };
    fetchFn();
  }, []);

  const backupPoper = (<PopOver>
    当渠道活码中所有客服人员都不在工作时间或者接待数量达到上限时，新客户将分配给该备用员工，备用员工默认全天在线且无接收数量上限。
  </PopOver>);

  return (
    <React.Fragment>
      <div className="drainage-editor-body">
        <div className="deb-title">渠道活码设置</div>
        <div className="deb-body">
          <div className="deb-line">
            <div className="deb-line-label required">二维码名称</div>
            <div className="deb-line-content">
              <LimitedInput {...codeName.props} />
            </div>
          </div>

          <div className="deb-line">
            <div className="deb-line-label required">
              <span>客服人员设置</span>
            </div>
            <div className="deb-line-content">
              <KfGroup {...serviceGroup.props} />
            </div>
          </div>

          <div className="deb-line">
            <div className="deb-line-label required">
              <span>备用员工</span>
              <OverlayTrigger trigger="hover" delay={100} overlay={backupPoper} placement="top">
                <i className="icon icon-help" />
              </OverlayTrigger>
            </div>
            <div className="deb-line-content">
              <Select {...backupMember.props} autoSearch={false} />
            </div>
          </div>

          <div className="deb-line">
            <div className="deb-line-label">
              <span>员工添加上限</span>
            </div>
            <div className="deb-line-content">
              <UpperLimit {...upperLimit.props} />
            </div>
          </div>

          <div className="deb-line">
            <div className="deb-line-label">
              <span style={{ lineHeight: 1.2, display: 'inline-block', width: '80%' }}>
                当日添加客户数补齐分配
              </span>
            </div>
            <div className="deb-line-content">
              <div style={{ display: 'flex', alignItems: 'center', height: 36 }}>
                <Switch {...supplement.props} />
                <span style={{ marginLeft: 8 }}>{supplement.active ? '开启' : '已关闭'}</span>
              </div>
            </div>
          </div>

          <div className="deb-line">
            <div className="deb-line-label">
              二维码预览
            </div>
            <div className="deb-line-content">
              <img width={210} src="https://wework.qpic.cn/wwpic/258649_JxkF9UzXSKmvfuT_1656482196/0" />
            </div>
          </div>
        </div>
      </div>
      <Footer onSubmit={handleSubmit} onCancel={handleCancel} mode={mode} />
    </React.Fragment>
  );
};

const Editor = (props) => {
  const mode = useMemo(() => {
    let _mode = 'create';
    if (/\/drainage\/edit\/:id$/.test(props.match.path)) _mode = PAGE_MODE.edit;
    if (/\/drainage\/detail\/:id$/.test(props.match.path)) _mode = PAGE_MODE.detail;
    return _mode;
  }, [props.match]);

  return (
    <div className="qw-drainage-editor">
      <Head {...props} mode={mode} />
      <Body {...props} mode={mode} />
    </div>
  );
};

export default Editor;
